import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  useProjects,
  validateSelectedProjects,
} from "@/contexts/ProjectContext";
import { PROJECT_SELECTION_CHANGED_EVENT } from "@/components/ProjectSwitcher";
import { llmApi } from "@/services/rag-service";
import { useProjectsQueryScope } from "@/features/projects/hooks/useProjectsQuery";
import {
  getKeycloakUserInfo,
  isKeycloakAuthenticated,
} from "@/services/keycloak";
import { isUserAdmin } from "@/utils/auth-helpers";
import CatalogPagination from "./components/CatalogPagination";
import CatalogTable from "./components/CatalogTable";
import CatalogToolbar from "./components/CatalogToolbar";
import { getSourceOptions, getTypeOptions } from "./constants/modelFields";
import AICatalogConfirmDeleteComp from "./ConfirmDelete";
import AICatalogDetailComp from "./Detail";
import AICatalogFormComp from "./Form";
import AICatalogShareComp from "./Share";
import useCopyToClipboard from "./hooks/useCopyToClipboard";
import type {
  CatalogOrder,
  LLMConfigsResponse,
  LLMModel,
  ModelFilterType,
  ModelSourceFilter,
  Provider,
} from "./types";
import {
  defaultProvider,
  getApiErrorMessage,
  sortModelsByName,
} from "./utils/catalog";

const AICatalogComp = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { cache, currentUser, userProfile, authProvider } = useAuth();
  const { selectedProjects } = useProjects();
  const { scope, initialized } = useProjectsQueryScope();

  const [detailOpen, setDetailOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [modelState, setModelState] = useState({
    session: null as object | null, models: [] as LLMModel[], pageCount: 0, loading: true,
  });
  const [provider, setProvider] = useState("");
  const [source, setSource] = useState<ModelSourceFilter>("");
  const [type, setType] = useState<ModelFilterType>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [order, setOrder] = useState<CatalogOrder>("asc");
  const [isAdmin, setIsAdmin] = useState(false);
  const [registerMenuOpen, setRegisterMenuOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<LLMModel | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [, setCurrentUserEmail] = useState("");
  const copyToClipboard = useCopyToClipboard();
  const shareTriggerRef = useRef<HTMLButtonElement | null>(null);
  const shareFallbackFocusRef = useRef<HTMLDivElement | null>(null);

  const sources = useMemo(() => getSourceOptions(t), [t]);
  const types = useMemo(() => getTypeOptions(t), [t]);
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects) as string[],
    [selectedProjects],
  );
  const [providerState, setProviderState] = useState({ session: null as object | null, providers: [] as Provider[] });

  const scopeIdentity = JSON.stringify([scope.principalId, scope.organizationId, initialized, safeSelectedProjects]);
  const scopeSession = useMemo(() => ({ identity: scopeIdentity }), [scopeIdentity]);
  const scopeReady = initialized === true && Boolean(scope.principalId);
  const requestIdentity = JSON.stringify([
    scopeIdentity, debouncedValue, provider, source, type, page, pageSize,
  ]);
  const requestSession = useMemo(() => ({ identity: requestIdentity }), [requestIdentity]);
  const activeScope = useRef<object | null>(null);
  const activeSession = useRef<object | null>(null);
  const modelRequest = useRef(0);
  const providerRequest = useRef(0);
  // Never render rows/options belonging to a previous committed scope, even
  // during the render before the next request's passive effect starts.
  const { models, pageCount, loading } = modelState.session === requestSession
    ? modelState : { models: [], pageCount: 0, loading: true };
  const providers = providerState.session === scopeSession ? providerState.providers : [];

  // Invalidate the old committed scope before passive effects or async settlement.
  // A new session object also distinguishes A → B → A from the original A.
  useLayoutEffect(() => {
    activeScope.current = scopeSession;
    return () => { activeScope.current = null; };
  }, [scopeSession]);

  useLayoutEffect(() => {
    activeSession.current = requestSession;
    return () => { activeSession.current = null; };
  }, [requestSession]);

  const fetchProviders = useCallback(async () => {
    if (activeScope.current !== scopeSession || !scopeReady) return;
    const request = ++providerRequest.current;
    const isCurrent = () => activeScope.current === scopeSession && providerRequest.current === request;
    try {
      const response = (await llmApi.getProviders()) as Provider[];
      if (!isCurrent()) return;
      setProviderState({ session: scopeSession, providers: response || [] });
    } catch (error) {
      if (!isCurrent()) return;
      toast.error(
        getApiErrorMessage(error, t("modelRegistration.failedToLoadProviders")),
      );
      setProviderState({ session: scopeSession, providers: [defaultProvider] });
    }
  }, [scopeSession, scopeReady, t]);

  const fetchModels = useCallback(async () => {
    if (activeSession.current !== requestSession || !scopeReady) return;
    const request = ++modelRequest.current;
    const isCurrent = () => activeSession.current === requestSession && modelRequest.current === request;
    setModelState({ session: requestSession, models: [], pageCount: 0, loading: true });
    try {
      const options: Record<string, unknown> = {
        page: Number(page) - 1,
        limit: pageSize,
      };

      if (debouncedValue.length) options.name = debouncedValue;
      if (safeSelectedProjects && !safeSelectedProjects.includes("all")) {
        options.project_ids = safeSelectedProjects;
      }
      if (type) options.type = type;
      if (provider) options.provider = provider;

      if (source === "shared") {
        options.include_shared = true;
      } else if (source === "bunyan") {
        options.include_public = true;
      } else if (source === "private") {
        options.include_public = false;
      }

      const response = (await llmApi.getLLMConfigs(
        options as any,
      )) as unknown as LLMConfigsResponse;
      if (!isCurrent()) return;
      setModelState({ session: requestSession, models: response.items || [],
        pageCount: Math.ceil((response?.total_count || 0) / (pageSize || 1)), loading: false });
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error fetching models:", error);
      toast.error(getApiErrorMessage(error, t("aiCatalog.failedToLoadModels")));
    } finally {
      if (isCurrent()) setModelState(current => ({ ...current, loading: false }));
    }
  }, [
    requestSession,
    scopeReady,
    safeSelectedProjects,
    debouncedValue,
    provider,
    source,
    type,
    page,
    pageSize,
    t,
  ]);

  const handleOpenForm = (id: string | null, nextIsPublic: boolean) => {
    setSelectedModelId(id);
    setRegisterMenuOpen(false);
    setIsPublic(nextIsPublic);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedModelId(null);
  };

  const handleOpenShareModal = (
    event: React.MouseEvent<HTMLElement>,
    model: LLMModel,
  ) => {
    event.stopPropagation();
    shareTriggerRef.current =
      event.currentTarget instanceof HTMLButtonElement
        ? event.currentTarget
        : null;
    setSelectedModel(model);
    setShareOpen(true);
  };

  const handleCloseShareModal = () => {
    setShareOpen(false);
    setSelectedModel(null);
  };

  const handleOpenDeleteModal = (model: LLMModel) => {
    setDeleteOpen(true);
    setSelectedModel(model);
  };

  const handleCloseDeleteModal = () => {
    setDeleteOpen(false);
    setSelectedModel(null);
  };

  const handleSortName = () => {
    setOrder((previous) => (previous === "asc" ? "desc" : "asc"));
  };

  const handleOpenDetail = (model: LLMModel) => {
    setSelectedModelId(model.id);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedModelId(null);
  };

  const resetToFirstPage = () => {
    setPage((currentPage) => (currentPage === 1 ? currentPage : 1));
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleProviderChange = (value: string) => {
    setProvider(value);
    resetToFirstPage();
  };

  const handleSourceChange = (value: ModelSourceFilter) => {
    setSource(value);
    resetToFirstPage();
  };

  const handleTypeChange = (value: ModelFilterType) => {
    setType(value);
    resetToFirstPage();
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    resetToFirstPage();
  };

  const sortedModels = useMemo(
    () => sortModelsByName(models, order),
    [models, order],
  );

  useEffect(() => {
    fetchModels();
    setIsAdmin(isUserAdmin());

    if (authProvider === "keycloak" && isKeycloakAuthenticated()) {
      const keycloakUser = getKeycloakUserInfo();
      if (keycloakUser?.email) setCurrentUserEmail(keycloakUser.email);
    } else if (currentUser?.email) {
      setCurrentUserEmail(currentUser.email);
    } else if (userProfile?.email) {
      setCurrentUserEmail(userProfile.email);
    }
  }, [currentUser, userProfile, authProvider, fetchModels]);

  useEffect(() => {
    window.addEventListener(PROJECT_SELECTION_CHANGED_EVENT, fetchModels);
    return () => {
      window.removeEventListener(PROJECT_SELECTION_CHANGED_EVENT, fetchModels);
    };
  }, [fetchModels]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    if (searchTerm === debouncedValue) return;

    const timer = window.setTimeout(() => {
      setDebouncedValue(searchTerm);
      resetToFirstPage();
    }, 500);

    return () => window.clearTimeout(timer);
  }, [searchTerm, debouncedValue]);

  return (
    <>
      <div
        ref={shareFallbackFocusRef}
        role="region"
        aria-label={t("aiCatalog.title")}
        tabIndex={-1}
        className="px-6 py-8"
      >
        <CatalogToolbar
          t={t}
          isRTL={isRTL}
          loading={loading}
          isAdmin={isAdmin}
          searchTerm={searchTerm}
          provider={provider}
          source={source}
          type={type}
          providers={providers}
          sources={sources}
          types={types}
          openMenu={registerMenuOpen}
          onSearchChange={handleSearchChange}
          onProviderChange={handleProviderChange}
          onSourceChange={handleSourceChange}
          onTypeChange={handleTypeChange}
          onMenuOpenChange={setRegisterMenuOpen}
          onOpenModal={handleOpenForm}
        />

        <CatalogTable
          t={t}
          isRTL={isRTL}
          loading={loading}
          models={sortedModels}
          currentUserId={cache?.id}
          order={order}
          onSortName={handleSortName}
          onShare={handleOpenShareModal}
          onCopyEndpoint={copyToClipboard}
          onOpenDetail={handleOpenDetail}
        />

        <CatalogPagination
          t={t}
          isRTL={isRTL}
          loading={loading}
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <AICatalogDetailComp
        selectedModelId={selectedModelId || undefined}
        open={detailOpen}
        onClose={handleCloseDetail}
        isAdmin={isAdmin}
        handleOpenModal={handleOpenForm}
        handleOpenDeleteModal={handleOpenDeleteModal}
      />

      <AICatalogFormComp
        selectedModelId={selectedModelId}
        isPublic={isPublic}
        open={formOpen}
        onClose={handleCloseForm}
        onSuccess={fetchModels}
      />

      <AICatalogShareComp
        key={selectedModel?.id || "ai-catalog-share"}
        open={shareOpen}
        onClose={handleCloseShareModal}
        model={selectedModel}
        onShared={fetchModels}
        returnFocusRef={shareTriggerRef}
        fallbackFocusRef={shareFallbackFocusRef}
      />

      <AICatalogConfirmDeleteComp
        selectedModel={selectedModel}
        open={deleteOpen}
        onClose={handleCloseDeleteModal}
        onSuccess={() => {
          handleCloseDetail();
          fetchModels();
        }}
      />
    </>
  );
};

export default AICatalogComp;
